import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import en from '../language/Eng.json';
import TopMenu from './menu/Top_Menu';
import Popup from './menu/popup';

const OPENTABLE_URL =
  'https://opentable.com.au/r/joie-brasserie-reservations-chatswood?restref=323204&lang=en-AU&ot_source=Restaurant%20website';

const Slide = styled.div<{ bg: string; active: boolean }>`
  position: fixed;
  inset: 0;
  background-image: url(${props => props.bg});
  background-size: cover;
  background-position: center;
  opacity: ${props => (props.active ? 1 : 0)};
  transition: opacity 1.5s ease-in-out;
  z-index: -2;
`;

const BackgroundVideo = styled.video<{ isLoaded: boolean }>`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
  opacity: ${props => (props.isLoaded ? 1 : 0)};
  transition: opacity 1s ease-in-out;
`;

const slideDownBanner = keyframes`
  0%   { transform: translateY(-150%) translateX(-50%); opacity: 0; }
  100% { transform: translateY(0)      translateX(-50%); opacity: 1; }
`;

const Banner = styled.div`
  position: fixed;
  top: 135px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(91, 44, 22, 0.85);
  color: #fceed1;
  padding: 18px 45px;
  border-radius: 8px;
  font-size: 1.1rem;
  letter-spacing: 2px;
  z-index: 900;
  animation: ${slideDownBanner} 1.2s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  width: max-content;
  max-width: 90vw;
  box-sizing: border-box;

  @media (max-width: 768px) {
    top: 110px;
    font-size: 0.75rem;
    padding: 12px 10px;
    width: 90vw;
    max-width: 340px;
    letter-spacing: 0;
  }
`;

const BannerLink = styled.a`
  font-size: 0.95rem;
  letter-spacing: 1px;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  display: block;
  transition: opacity 0.3s ease;
  word-break: break-all;

  &:hover {
    opacity: 0.7;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    letter-spacing: 0;
  }
`;

const OuterContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 200px 20px 80px;
  box-sizing: border-box;
  overflow-x: hidden;
  gap: 40px;

  @media (max-width: 768px) {
    padding: 220px 15px 60px;
    gap: 28px;
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  max-width: 1400px;
  gap: 24px;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 800px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    max-width: 420px;
    width: 92%;
    gap: 16px;
  }
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, rgba(158, 94, 4, 0.35) 0%, rgba(91, 44, 22, 0.5) 100%);
  padding: 36px 32px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #fff;
  cursor: pointer;
  transition: all 0.4s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 28px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 230px;
  box-sizing: border-box;

  &:hover {
    background: linear-gradient(135deg, rgba(158, 94, 4, 0.55) 0%, rgba(91, 44, 22, 0.7) 100%);
    border: 1px solid rgba(255, 255, 255, 0.25);
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  }

  @media (max-width: 768px) {
    padding: 28px 24px;
    gap: 20px;
    min-height: 190px;
  }
`;

const ReservationCard = styled(InfoCard)`
  background: linear-gradient(135deg, rgba(91, 44, 22, 0.5) 0%, rgba(158, 94, 4, 0.4) 100%);
  border: 1px solid rgba(252, 238, 209, 0.2);
  justify-content: flex-start;

  &:hover {
    background: linear-gradient(135deg, rgba(91, 44, 22, 0.7) 0%, rgba(158, 94, 4, 0.6) 100%);
    border: 1px solid rgba(252, 238, 209, 0.4);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CardTitle = styled.h3`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.25rem;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  margin: 0;
`;

const CardArrow = styled.div`
  font-size: 1rem;
  color: #fceed1;
  font-weight: bold;
  opacity: 0.8;
  transition: opacity 0.3s ease, transform 0.3s ease;

  ${InfoCard}:hover & {
    opacity: 1;
    transform: translate(2px, -2px);
  }
`;

const CardText = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.45;
  }
`;

const HomeHoursBox = styled.div`
  background-color: rgba(91, 44, 22, 0.6);
  color: #fceed1;
  padding: 22px 44px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  max-width: 820px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 768px) {
    padding: 20px;
    width: 92%;
    gap: 10px;
  }
`;

const HoursTitle = styled.div`
  font-weight: bold;
  font-size: 1rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #fceed1;

  @media (max-width: 768px) {
    font-size: 0.88rem;
    letter-spacing: 1.5px;
  }
`;

const HoursText = styled.div`
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: pre-line;
  color: rgba(252, 238, 209, 0.9);
  font-weight: 300;
  letter-spacing: 0.4px;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.4;
  }
`;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [videoLoaded, setVideoLoaded] = React.useState(false);

  const USE_BANNER = true;

  const images = [
    'image/background/back8.webp',
    'image/background/back9.webp',
    'image/background/back10.webp',
    'image/background/back11.webp',
    'image/background/back12.webp',
  ];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <>
      <TopMenu />
      <Popup />

      {USE_BANNER && (
        <Banner>
          <div style={{ fontWeight: 'bold', marginBottom: '4px', wordBreak: 'keep-all' }}>
            {en.home.banner.title}
          </div>
          <BannerLink href="tel:0449993950">{en.home.banner.tel}</BannerLink>
          <BannerLink
            href={OPENTABLE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {en.home.banner.bookTable}
          </BannerLink>
        </Banner>
      )}

      <OuterContainer>
        {images.map((img, index) => (
          <Slide key={index} bg={img} active={currentSlide === index} />
        ))}

        <BackgroundVideo
          autoPlay
          muted
          loop
          playsInline
          src="video/background/bg.mp4"
          isLoaded={videoLoaded}
          onCanPlayThrough={() => setVideoLoaded(true)}
        />

        <ContentContainer>
          <InfoCard onClick={() => navigate('/menu')}>
            <CardHeader>
              <CardTitle>{en.home.leftLink}</CardTitle>
              <CardArrow>↗</CardArrow>
            </CardHeader>
            <CardText>{en.home.leftCard}</CardText>
          </InfoCard>

          <InfoCard onClick={() => navigate('/about')}>
            <CardHeader>
              <CardTitle>{en.home.rightLinkTop}</CardTitle>
              <CardArrow>↗</CardArrow>
            </CardHeader>
            <CardText>{en.home.rightCardTop}</CardText>
          </InfoCard>

          <InfoCard onClick={() => navigate('/contact')}>
            <CardHeader>
              <CardTitle>{en.home.rightLinkBottom}</CardTitle>
              <CardArrow>↗</CardArrow>
            </CardHeader>
            <CardText>{en.home.rightCardBottom}</CardText>
          </InfoCard>

          <ReservationCard
            onClick={() => window.open(OPENTABLE_URL, '_blank', 'noopener,noreferrer')}
          >
            <CardHeader>
              <CardTitle>{en.home.reservationLink}</CardTitle>
              <CardArrow>↗</CardArrow>
            </CardHeader>
            <CardText>{en.home.reservationCard}</CardText>
          </ReservationCard>
        </ContentContainer>

        <HomeHoursBox>
          <HoursTitle>{en.bottom.openingTimesTitle}</HoursTitle>
          <HoursText>
            {en.bottom.openingTimesShort}
            {'\n\n'}
            {en.bottom.openingTimesLong}
          </HoursText>
        </HomeHoursBox>
      </OuterContainer>
    </>
  );
};

export default Home;